import axios from 'axios';
import { toast } from 'react-toastify';
import * as _ from 'lodash';
import { logout } from '../actions/auth';
import { req, res } from '../actions/ui';
import store from '../store';
import i18n from '../i18n';

const BASEURL = process.env.REACT_APP_API_URL;

// Business error codes returned by the API (e.g. EMAIL_ALREADY_IN_USE,
// DUPLICATE_ENTRY) always look like this. Free-text error messages (the
// API's fallback for anything not mapped to a business code, which today
// includes raw driver/DB errors) never do.
export const BUSINESS_ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

// If the API sent a business error code we don't have a translation for,
// show a generic friendly message instead of leaking the raw key (or, for
// untranslated free-text messages such as raw SQL errors, the raw text
// itself) straight to the user.
export function resolveErrorMessage(data) {
  const key = data.message || data.error;

  const isUntranslatedBusinessErrorCode =
    BUSINESS_ERROR_CODE_PATTERN.test(data.error) && !i18n.exists(key);

  return i18n.t(isUntranslatedBusinessErrorCode ? 'GENERIC_ERROR' : key);
}

export function catchError(error) {
  if (!error.response) {
    console.log(error);

    return;
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      store.dispatch(logout());
      window.location.href = '/login';
      break;
    default: {
      toast.error(resolveErrorMessage(data));
    }
  }

  store.dispatch(res());

  throw error;
}

function removeEmptyQs(params) {
  return Object.keys(params).reduce((acc, key) => {
    if (params[key] || params[key] === 0) {
      acc[key] = params[key];
    }

    return acc;
  }, {});
}

function buildOptions({ method, params, body, path }) {
  const options = {
    method,
    url: `${BASEURL}${path}`,
    headers: {},
  };

  if (localStorage.getItem('token')) {
    options.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
  }

  if (params) {
    if (params.where) {
      params.where = removeEmptyQs(params.where);
    } else {
      params = {
        ..._.pick(params, ['attributes', 'limit', 'offset', 'order']),
        ...removeEmptyQs(_.omit(params, ['attributes', 'limit', 'offset', 'order'])),
      };
    }

    options.params = params;
  }

  if (body) {
    options.data = Object.keys(body).reduce((acc, key) => {
      if (body[key] === '__none__') {
        return acc;
      }

      acc[key] = body[key];

      return acc;
    }, {});
  }

  return options;
}

export default function request(requestOptions) {
  const options = buildOptions(requestOptions);

  store.dispatch(req());

  return axios(options)
    .then((response) => {
      store.dispatch(res());

      return response;
    })
    .catch(catchError);
}

// Same request without catchError: no toast and no logout + reload on 401.
// Every error goes back to the caller, which owns its handling (e.g. a 401
// that must keep the post-login return path). The loading counter is
// balanced on both paths, otherwise the global <Loading> overlay would stay on.
export function requestRaw(requestOptions) {
  const options = buildOptions(requestOptions);

  store.dispatch(req());

  return axios(options).then(
    (response) => {
      store.dispatch(res());

      return response;
    },
    (error) => {
      store.dispatch(res());

      throw error;
    }
  );
}

export function sseListen({ path, onData, onError }) {
  const authToken = localStorage.getItem('token');

  const url = `${BASEURL}${path}`.replace(/\?$/, '') + `?jwt=${authToken}`;
  const events = new EventSource(url);

  events.addEventListener(
    'message',
    (event) => {
      try {
        onData(JSON.parse(event.data));
      } catch (error) {
        console.error('[access-code-sse] Error in data parse', error);
        onError && onError(error);
      }
    },
    false
  );

  events.addEventListener(
    'open',
    () => {
      console.log('[access-code-sse] Opened stream');
    },
    false
  );

  events.addEventListener(
    'error',
    (error) => {
      console.error('[access-code-sse] Error in stream', error);
      onError && onError(error);
    },
    false
  );

  return {
    close: () => {
      events.close();
    },
  };
}
